import type {MessageAttachment} from '@microsoft/agent-host-protocol'
import type {CopilotChatReference, FileDiffReference, FileReference} from '@github-ui/copilot-chat-types'

import {implicitReferenceToAttachment} from './implicit-context'

/** Picks the subset of an immersive `FileReference` the SDK diff side needs. */
function fileReferenceToSide(file: FileReference) {
  return {
    path: file.path,
    ref: file.ref,
    commitOID: file.commitOID,
    repoID: file.repoID,
    repoOwner: file.repoOwner,
    repoName: file.repoName,
  }
}

function fileDiffUrl(ref: FileDiffReference): string {
  if (!ref.pullRequestUrl) return ref.url

  const diffAnchor = ref.id.startsWith('#') ? ref.id : `#${ref.id}`
  return `${ref.pullRequestUrl.replace(/\/$/, '')}/files${diffAnchor}`
}

function selectedDiffSnippet(ref: FileDiffReference): MessageAttachment | undefined {
  const startMatch = ref.selectedRange?.start?.match(/^([LR])([1-9]\d*)$/)
  const endMatch = (ref.selectedRange?.end ?? ref.selectedRange?.start)?.match(/^([LR])([1-9]\d*)$/)
  if (!startMatch || !endMatch || startMatch[1] !== endMatch[1]) return undefined

  const file = startMatch[1] === 'L' ? ref.baseFile : ref.headFile
  if (!file) return undefined

  const start = Number(startMatch[2])
  const end = Number(endMatch[2])
  return implicitReferenceToAttachment({
    ...file,
    type: 'snippet',
    range: {start: Math.min(start, end), end: Math.max(start, end)},
  })
}

/**
 * Converts an immersive `FileDiffReference` — published by the shared diff-view
 * "Ask Copilot" / "Attach to thread" affordances (`@github-ui/copilot-code-chat`)
 * over `@github-ui/copilot-chat-events` — into the AHP attachment quick chat
 * submits. Selected lines become snippets because `github_file_diff` has no
 * supported line-range field; references without a same-side selection remain
 * whole-file diffs.
 */
export function fileDiffReferenceToAttachment(ref: FileDiffReference): MessageAttachment | undefined {
  const snippet = selectedDiffSnippet(ref)
  if (snippet) return snippet

  return implicitReferenceToAttachment({
    type: 'file-diff',
    url: fileDiffUrl(ref),
    baseFile: ref.baseFile ? fileReferenceToSide(ref.baseFile) : null,
    headFile: ref.headFile ? fileReferenceToSide(ref.headFile) : null,
  })
}

/**
 * Converts a `CopilotChatReference` carried on the shared chat events into an
 * AHP attachment. Explicit code-view and diff-view references share the same
 * mapping as references resolved through the implicit-context endpoint.
 */
export function copilotChatReferenceToAttachment(ref: CopilotChatReference): MessageAttachment | undefined {
  switch (ref.type) {
    case 'file':
    case 'file-v2':
    case 'snippet':
      return implicitReferenceToAttachment(ref)
    case 'file-diff':
      return fileDiffReferenceToAttachment(ref)
    default:
      return undefined
  }
}
